import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

export interface RadioTrack {
  id: string;
  title: string;
  artist?: string;
  fileName: string;
  fileUrl: string;
  duration?: number;
  uploadedAt: Date;
  uploadedBy: string;
  uploadedByName: string;
  orgId: string;
  isActive: boolean;
}

class RadioService {
  private collection = 'radioTracks';

  async uploadTrack(
    file: File,
    metadata: {
      title: string;
      artist?: string;
      orgId: string;
      uploadedBy: string;
      uploadedByName: string;
    }
  ): Promise<RadioTrack> {
    try {
      // Upload file to Firebase Storage
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `radio/${metadata.orgId}/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      // Create track document
      const trackData = {
        title: metadata.title,
        artist: metadata.artist || 'Unknown Artist',
        fileName,
        fileUrl,
        uploadedAt: new Date(),
        uploadedBy: metadata.uploadedBy,
        uploadedByName: metadata.uploadedByName,
        orgId: metadata.orgId,
        isActive: true
      };

      const docRef = await addDoc(collection(db, this.collection), trackData);

      return {
        id: docRef.id,
        ...trackData
      };
    } catch (error) {
      console.error('Error uploading track:', error);
      throw error;
    }
  }

  async getTracks(orgId: string): Promise<RadioTrack[]> {
    try {
      const q = query(
        collection(db, this.collection),
        orderBy('uploadedAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const tracks = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          uploadedAt: doc.data().uploadedAt?.toDate() || new Date()
        } as RadioTrack))
        .filter(track => track.orgId === orgId);

      return tracks;
    } catch (error) {
      console.error('Error getting tracks:', error);
      return [];
    }
  }

  async getActiveTracks(orgId: string): Promise<RadioTrack[]> {
    const tracks = await this.getTracks(orgId);
    return tracks.filter(t => t.isActive);
  }

  async deleteTrack(trackId: string, fileName: string, orgId: string): Promise<void> {
    try {
      // Delete from Storage
      const storageRef = ref(storage, `radio/${orgId}/${fileName}`);
      await deleteObject(storageRef);

      // Delete from Firestore
      await deleteDoc(doc(db, this.collection, trackId));
    } catch (error) {
      console.error('Error deleting track:', error);
      throw error;
    }
  }

  async toggleTrackActive(trackId: string, isActive: boolean): Promise<void> {
    try {
      await updateDoc(doc(db, this.collection, trackId), { isActive });
    } catch (error) {
      console.error('Error toggling track:', error);
      throw error;
    }
  }
}

export const radioService = new RadioService();

